import { MAX_KEYBINDINGS_COUNT, MAX_WHEN_EXPRESSION_DEPTH, THREAD_JUMP_KEYBINDING_COMMANDS, } from "@t3tools/contracts";
export const DEFAULT_KEYBINDINGS = [
    { key: "mod+k", command: "commandPalette.toggle" },
    { key: "mod+n", command: "chat.new" },
    { key: "mod+shift+o", command: "chat.new" },
    { key: "mod+shift+n", command: "chat.newLocal" },
    { key: "mod+shift+[", command: "thread.previous" },
    { key: "mod+shift+]", command: "thread.next" },
    ...THREAD_JUMP_KEYBINDING_COMMANDS.map((command, index) => ({
        key: `mod+${index + 1}`,
        command,
    })),
];
function normalizeKeyToken(token) {
    if (token === "space")
        return " ";
    if (token === "esc")
        return "escape";
    return token;
}
export function parseKeybindingShortcut(value) {
    const rawTokens = value
        .toLowerCase()
        .split("+")
        .map((token) => token.trim());
    const tokens = [...rawTokens];
    let trailingEmptyCount = 0;
    while (tokens[tokens.length - 1] === "") {
        trailingEmptyCount += 1;
        tokens.pop();
    }
    if (trailingEmptyCount > 0) {
        tokens.push("+");
    }
    if (tokens.some((token) => token.length === 0)) {
        return null;
    }
    if (tokens.length === 0)
        return null;
    let key = null;
    let metaKey = false;
    let ctrlKey = false;
    let shiftKey = false;
    let altKey = false;
    let modKey = false;
    for (const token of tokens) {
        switch (token) {
            case "cmd":
            case "meta":
                metaKey = true;
                break;
            case "ctrl":
            case "control":
                ctrlKey = true;
                break;
            case "shift":
                shiftKey = true;
                break;
            case "alt":
            case "option":
                altKey = true;
                break;
            case "mod":
                modKey = true;
                break;
            default: {
                if (key !== null)
                    return null;
                key = normalizeKeyToken(token);
            }
        }
    }
    if (key === null)
        return null;
    return {
        key,
        metaKey,
        ctrlKey,
        shiftKey,
        altKey,
        modKey,
    };
}
function tokenizeWhenExpression(expression) {
    const tokens = [];
    let index = 0;
    while (index < expression.length) {
        const current = expression[index];
        if (!current)
            break;
        if (/\s/.test(current)) {
            index += 1;
            continue;
        }
        if (expression.startsWith("&&", index)) {
            tokens.push({ type: "and" });
            index += 2;
            continue;
        }
        if (expression.startsWith("||", index)) {
            tokens.push({ type: "or" });
            index += 2;
            continue;
        }
        if (current === "!") {
            tokens.push({ type: "not" });
            index += 1;
            continue;
        }
        if (current === "(") {
            tokens.push({ type: "lparen" });
            index += 1;
            continue;
        }
        if (current === ")") {
            tokens.push({ type: "rparen" });
            index += 1;
            continue;
        }
        const identifier = /^[A-Za-z_][A-Za-z0-9_.-]*/.exec(expression.slice(index));
        if (!identifier) {
            return null;
        }
        tokens.push({ type: "identifier", value: identifier[0] });
        index += identifier[0].length;
    }
    return tokens;
}
export function parseKeybindingWhenExpression(expression) {
    const tokens = tokenizeWhenExpression(expression);
    if (!tokens || tokens.length === 0)
        return null;
    let index = 0;
    const parsePrimary = (depth) => {
        if (depth > MAX_WHEN_EXPRESSION_DEPTH) {
            return null;
        }
        const token = tokens[index];
        if (!token)
            return null;
        if (token.type === "identifier") {
            index += 1;
            return { type: "identifier", name: token.value };
        }
        if (token.type === "lparen") {
            index += 1;
            const expressionNode = parseOr(depth + 1);
            const closeToken = tokens[index];
            if (!expressionNode || !closeToken || closeToken.type !== "rparen") {
                return null;
            }
            index += 1;
            return expressionNode;
        }
        return null;
    };
    const parseUnary = (depth) => {
        let notCount = 0;
        while (tokens[index]?.type === "not") {
            index += 1;
            notCount += 1;
            if (notCount > MAX_WHEN_EXPRESSION_DEPTH) {
                return null;
            }
        }
        let node = parsePrimary(depth);
        if (!node)
            return null;
        while (notCount > 0) {
            node = { type: "not", node };
            notCount -= 1;
        }
        return node;
    };
    const parseAnd = (depth) => {
        let left = parseUnary(depth);
        if (!left)
            return null;
        while (tokens[index]?.type === "and") {
            index += 1;
            const right = parseUnary(depth);
            if (!right)
                return null;
            left = { type: "and", left, right };
        }
        return left;
    };
    const parseOr = (depth) => {
        let left = parseAnd(depth);
        if (!left)
            return null;
        while (tokens[index]?.type === "or") {
            index += 1;
            const right = parseAnd(depth);
            if (!right)
                return null;
            left = { type: "or", left, right };
        }
        return left;
    };
    const ast = parseOr(0);
    if (!ast || index !== tokens.length)
        return null;
    return ast;
}
export function compileResolvedKeybindingRule(rule) {
    const shortcut = parseKeybindingShortcut(rule.key);
    if (!shortcut)
        return null;
    if (rule.when !== undefined) {
        const whenAst = parseKeybindingWhenExpression(rule.when);
        if (!whenAst)
            return null;
        return {
            command: rule.command,
            shortcut,
            whenAst,
        };
    }
    return {
        command: rule.command,
        shortcut,
    };
}
export function compileResolvedKeybindingsConfig(config) {
    const compiled = [];
    for (const rule of config) {
        const result = compileResolvedKeybindingRule(rule);
        if (result) {
            compiled.push(result);
        }
    }
    return compiled.slice(-MAX_KEYBINDINGS_COUNT);
}
export const DEFAULT_RESOLVED_KEYBINDINGS = compileResolvedKeybindingsConfig(DEFAULT_KEYBINDINGS);
